import React from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Types
import { User } from 'Shared/constants/types';
import { BaInfoStatus } from 'Shared/types/BaInfo.types';
import { BaseCtaShowProps } from './_partials/BaseCta';

// Components
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import FetchingBaProfile from './_partials/FetchingBaProfile';
import BaProfileDisplay, { Props as BaProfileDisplayProps } from './_partials/BaProfileDisplay';
import BaPending from './_partials/BaPending';
import BaDoesNotExist from './_partials/BaDoesNotExist';
import SendBa from 'Shared/components/UserBaProfileSnapshot/_partials/SendBa';
import ResendBa from 'Shared/components/UserBaProfileSnapshot/_partials/ResendBa';

// Hooks
import { useBaProfile } from 'Shared/utils/hooks/use-ba-profile';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

export interface Props extends BaProfileDisplayProps, BaseCtaShowProps {
  user: User;
  inContainer?: boolean;
}

const UserBaProfileSnapshot = ({ user, inContainer = true, ...rest }: Props) => {
  const { id: currentUserId } = useCurrentUser();
  const { status, isFetching, hasPendingBa, baProfile, hasPendingAssesmentId, referencePatternName } = useBaProfile({
    user,
  });

  const isLookingAtOwnBA = currentUserId === user.id;

  const { featureFlagShowRelationshipGuideInWorkspace } = useFlags();

  /* if we're not looking at our own profile, and the person
    doesn't have a BA, don't render anything
   ( Only applies when featureFlagShowRelationshipGuideInWorkspace is turned off)
  */
  if (
    !featureFlagShowRelationshipGuideInWorkspace &&
    !isFetching &&
    !isLookingAtOwnBA &&
    (status === BaInfoStatus.BA_NOT_FOUND || !referencePatternName || hasPendingBa || hasPendingAssesmentId)
  )
    return null;

  const content = (
    <>
      {isFetching && <FetchingBaProfile />}

      {!isFetching && !isLookingAtOwnBA && (hasPendingBa || hasPendingAssesmentId) && <ResendBa user={user} />}

      {!isFetching &&
        !isLookingAtOwnBA &&
        !hasPendingBa &&
        !hasPendingAssesmentId &&
        (status === BaInfoStatus.BA_NOT_FOUND || !referencePatternName) && <SendBa user={user} {...rest} />}

      {!isFetching && status === BaInfoStatus.BA_NOT_FOUND && !hasPendingAssesmentId && isLookingAtOwnBA && (
        <BaDoesNotExist user={user} {...rest} />
      )}
      {(hasPendingAssesmentId || hasPendingBa) && isLookingAtOwnBA && <BaPending user={user} {...rest} />}
      {!isFetching && baProfile && !hasPendingBa && baProfile?.referencePattern && (
        <BaProfileDisplay user={user} {...rest} />
      )}
    </>
  );

  if (inContainer)
    return (
      <WpContainer>
        <WpContainerBody>{content}</WpContainerBody>
      </WpContainer>
    );

  return content;
};

export default UserBaProfileSnapshot;
